import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { ArchiveRestore, Trash2 } from 'lucide-react';
import { useConfirmation } from '@/components/shared/ZCard';
import OverflowMenu from '@/components/shared/OverflowMenu';
import ZEmptyState from '@/components/shared/ZEmptyState';
import ZLoadingBar from '@/components/shared/ZLoadingBar';
import Logotype from '@/components/shared/Logotype';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

const ACCENT = '#C0A96A';

export default function SoulSyncLibraryArchive() {
  const navigate = useNavigate();
  const feedback = useConfirmation();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadArchived();
  }, []);

  const loadArchived = async () => {
    try {
      const { data } = await api.get('/library/books?status=archived&limit=200');
      setBooks(data);
    } catch { /* silent — empty state handles it */ }
    finally { setLoading(false); }
  };

  const restoreBook = async (id) => {
    const prev = books;
    setBooks(books.filter(b => b.id !== id));
    try {
      await api.put(`/library/books/${id}`, { status: 'active' });
      feedback.confirm();
    } catch {
      setBooks(prev);
      feedback.fail();
    }
  };

  const hardDelete = async (id) => {
    const prev = books;
    setBooks(books.filter(b => b.id !== id));
    setDeleteConfirm(null);
    try {
      await api.delete(`/library/books/${id}?hard=true`);
      feedback.confirm();
    } catch {
      setBooks(prev);
      feedback.fail();
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-3xl mx-auto pb-40">
      {/* Breadcrumb */}
      <div style={{ marginBottom: 20 }}>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                onClick={() => navigate('/soulsync')}
                style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <Logotype app="soulsync" size="sidebar" />
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink
                onClick={() => navigate('/library')}
                style={{ cursor: 'pointer', color: '#6b7084', fontSize: 13, fontFamily: "'Inter', sans-serif" }}
              >
                Library
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage style={{ color: '#f0f0f5', fontSize: 13, fontFamily: "'Inter', sans-serif" }}>
                Archive
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <ZLoadingBar accent={ACCENT} loading={loading} />

      {!loading && books.length === 0 && (
        <ZEmptyState
          mark="L"
          accent={ACCENT}
          message="No archived books"
          hint="Archived books will appear here. They can be restored or permanently deleted."
        />
      )}

      {books.map((book) => {
        const isConfirming = deleteConfirm === book.id;

        return (
          <div
            key={book.id}
            style={{
              position: 'relative',
              background: 'rgba(255,255,255,0.03)',
              border: '0.5px solid rgba(255,255,255,0.06)',
              borderLeft: `2px solid ${ACCENT}44`,
              borderRadius: 10,
              padding: 14,
              marginBottom: 8,
              opacity: 0.7,
            }}
          >
            {!isConfirming && (
              <OverflowMenu
                items={[
                  { icon: <ArchiveRestore size={16} strokeWidth={1.5} />, label: 'Restore', onClick: () => restoreBook(book.id) },
                  { icon: <Trash2 size={16} strokeWidth={1.5} />, label: 'Delete permanently', onClick: () => setDeleteConfirm(book.id), destructive: true },
                ]}
              />
            )}

            {/* Book content */}
            <div style={{
              fontSize: 13, fontWeight: 500, color: '#f0f0f5',
              fontFamily: "'Playfair Display', serif", lineHeight: 1.4,
              paddingRight: 32,
            }}>
              {book.title}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
              {book.year && (
                <span style={{
                  fontSize: 10, color: '#6b7084',
                  fontFamily: "'JetBrains Mono', monospace",
                }}>
                  {book.year}
                </span>
              )}
              {book.series && (
                <span style={{
                  fontSize: 10, padding: '2px 7px', borderRadius: 4,
                  background: 'rgba(255,255,255,0.04)', color: '#7A7A9A',
                }}>
                  {book.series}
                </span>
              )}
            </div>

            {/* Inline destructive confirmation (7.1 pattern) */}
            {isConfirming && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginTop: 10,
                paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.04)',
              }}>
                <button
                  onClick={() => hardDelete(book.id)}
                  style={{
                    background: '#E24B4A', border: 'none', borderRadius: 8,
                    color: '#ffffff', fontFamily: "'Inter', sans-serif",
                    fontSize: 12, fontWeight: 600, padding: '8px 16px',
                    cursor: 'pointer',
                  }}
                >
                  Delete permanently
                </button>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  style={{
                    background: 'none', border: 'none',
                    color: '#6b7084', fontFamily: "'Inter', sans-serif",
                    fontSize: 12, cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
